import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { FixedSizeList } from 'react-window';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import Autocomplete from "@material-ui/lab/Autocomplete";

import FilterList from '../store/FilterList';


const styles = theme => ({
  filterList: {
    width: 300,
  },
  inputAdornment: {
    position: 'absolute',
    right: 0,
  },
  listbox: {
    padding: 0,
  },
});


const OuterElementContext = React.createContext({});


const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});


@withStyles(styles)
@observer
class FilterListInput extends React.Component {
  static propTypes = {
    filter: PropTypes.string.isRequired,
    filterList: PropTypes.instanceOf(FilterList).isRequired,
    InputProps: PropTypes.shape(),
  };

  static defaultProps = {
    InputProps: {},
  };

  render() {
    const { filter, InputProps, filterList, classes, ...props } = this.props;
    return (
      <ListItem>
        <TextField
          {...props}
          placeholder={filter}
          fullWidth
          InputProps={{
            ...InputProps,
            endAdornment: (
              <InputAdornment position="end" className={classes.inputAdornment}>
                {filterList.isFilterOpen(filter)
                  ? (
                    <IconButton onClick={() => filterList.closeFilter(filter)} size="small" tabIndex="-1">
                      <ArrowDropUpIcon />
                    </IconButton>
                  )
                  : (
                    <IconButton onClick={() => filterList.openFilter(filter)} size="small" tabIndex="-1">
                      <ArrowDropDownIcon />
                    </IconButton>
                  )
                }
              </InputAdornment>
            )
          }}
        />
      </ListItem>
    );
  }
}


@observer
class FilterListPopper extends React.Component {
  static propTypes = {
    filter: PropTypes.string.isRequired,
    filterList: PropTypes.instanceOf(FilterList).isRequired,
  };

  render() {
    const { filter, filterList, ...props } = this.props;
    if ('anchorEl' in props) {
      delete props.anchorEl;
    }
    if ('style' in props) {
      delete props.style;
    }
    return <Collapse {...props} in={filterList.isFilterOpen(filter)} />;
  }
}


@withStyles(styles)
@observer
class FilterListView extends React.Component {
  static propTypes = {
    filterList: PropTypes.instanceOf(FilterList).isRequired,
  };

  open = filter => () => {
    const { filterList } = this.props;
    filterList.openFilter(filter)
  };

  renderListBox = React.forwardRef(({ children, ...other }, ref) => {
    const itemData = React.Children.toArray(children);
    const itemSize = 50;
    const itemCount = itemData.length;
    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            itemData={itemData}
            height={Math.min(itemCount * itemSize, 6 * itemSize)}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={itemSize}
            itemCount={itemCount}
          >
            {({ data, index, style }) => React.cloneElement(data[index], { style })}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  renderFilter = filter => {
    const { filterList, classes } = this.props;
    const values = [...filterList.getCheckedValues(filter)];
    const options = [...new Set([...filterList.getAvailableValues(filter), ...values])];
    return (
      <Autocomplete
        key={filter}
        multiple
        options={options}
        value={values}
        onChange={(event, values) => filterList.setValues(filter, values)}
        disableListWrap
        disableClearable
        // forcePopupIcon={false}
        onInputChange={this.open(filter)}
        onFocus={this.open(filter)}
        disableCloseOnSelect
        open
        renderInput={props => <FilterListInput {...props} filter={filter} filterList={filterList} />}
        PopperComponent={props => <FilterListPopper {...props} filter={filter} filterList={filterList} />}
        PaperComponent={({ children }) => <div>{children}</div>}
        ListboxComponent={this.renderListBox}
        renderOption={option => <Typography noWrap>{option}</Typography>}
        size="small"
        getOptionLabel={option => `${option}`}
        classes={{
          listbox: classes.listbox,
        }}
      />
    );
  };

  render() {
    const { classes, filterList } = this.props;
    return (
      <>
        <Tooltip title="Filter">
          <IconButton onClick={event => filterList.open(event)}>
            <Badge badgeContent={filterList.count()} color="primary">
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover
          open={filterList.isOpen}
          onClose={filterList.close}
          anchorEl={filterList.anchorEl}
          anchorOrigin={{vertical: 'top', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
        >
          <List disablePadding className={classes.filterList}>
            {Object.keys(filterList.availableFilters).map(this.renderFilter)}
          </List>
          <Divider />
          <Button
            fullWidth
            size="large"
            onClick={filterList.clear}
            disabled={filterList.count() === 0}
          >
            Clear
          </Button>
        </Popover>
      </>
    );
  }
}


export default FilterListView;
